import { router, useLocalSearchParams, Redirect } from 'expo-router';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { Image, StyleSheet, Text as RNText, View } from 'react-native';

import { BackChevron } from '@/components/BackChevron';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Heading } from '@/components/Heading';
import { Overline } from '@/components/Overline';
import { Screen } from '@/components/Screen';
import { Text } from '@/components/Text';
import { GiftFallbackScreen } from '@/components/gifting/GiftFallbackScreen';
import { colors, fonts, fontSizes, radii, spacing } from '@/constants/theme';
import { GIFTING_ROUTES, giftingRouteFor } from '@/lib/giftingRoute';
import { authStore } from '@/stores/auth.store';
import { giftingStore } from '@/stores/gifting.store';

/**
 * Product detail. Sold-out products render fully (image, copy) with a
 * disabled CTA + back-to-gifts affordance — a direct link to a sold-out
 * product must never 404 (they stay in the list payload by design).
 */
const GiftProductDetailScreen = observer(() => {
  const { id } = useLocalSearchParams<{ id?: string }>();
  const productId = Number(id);
  const product = giftingStore.productById(productId);
  useEffect(() => {
    void giftingStore.ensureLoaded();
  }, []);

  // Logged-out deep links go through the campaign door (review finding).
  // After all hooks — auth state changes at runtime, unlike Platform.OS.
  if (authStore.hydrated && !authStore.user) {
    return <Redirect href={GIFTING_ROUTES.landing} />;
  }

  // Product detail belongs to the choosing phase: render only while the
  // server routes this user to the grid. Without this, an ineligible user
  // could deep-link here, continue to the shipping form, and only be refused
  // at the final claim — the exact fill-then-403 class the funnel fix closed
  // on the other screens (adversarial review finding).
  const step = giftingRouteFor(authStore.user);
  if (authStore.hydrated && step !== GIFTING_ROUTES.choose) {
    return <Redirect href={step ?? GIFTING_ROUTES.landing} />;
  }

  if (!product) {
    return <GiftFallbackScreen />;
  }

  const soldOut = product.sold_out;

  return (
    <Screen width="column">
      <BackChevron onPress={() => router.replace('/season-of-gifting/choose')} />
      <View style={styles.body}>
        <View style={styles.imageWrap}>
          {product.image_url ? (
            <Image source={{ uri: product.image_url }} style={styles.image} resizeMode="cover" />
          ) : (
            <View style={styles.imageFallback}>
              <RNText style={styles.imageFallbackText}>{product.name.slice(0, 1)}</RNText>
            </View>
          )}
        </View>

        <Overline style={styles.category}>{product.category}</Overline>
        <Heading size="3xl">
          {product.name}
        </Heading>
        {product.description ? (
          <Text color="textMuted" style={styles.description}>
            {product.description}
          </Text>
        ) : null}

        <Card style={styles.receiveCard}>
          <Overline>What you'll receive</Overline>
          <Text variant="bodyMedium" style={styles.receiveText}>
            {product.receive_text}
          </Text>
        </Card>

        <Text variant="caption" color="textMuted" style={styles.noPost}>
          No social post is required.
        </Text>

        <Button
          label={soldOut ? 'Sold out' : `Claim ${product.name}`}
          onPress={() =>
            router.push({
              pathname: '/season-of-gifting/confirm',
              params: { productId: String(product.id) },
            })
          }
          disabled={soldOut}
          style={styles.cta}
        />
        {soldOut && (
          <Button
            label="Back to gifts"
            variant="outline"
            onPress={() => router.replace('/season-of-gifting/choose')}
            style={styles.backCta}
          />
        )}
      </View>
    </Screen>
  );
});

export default GiftProductDetailScreen;

const styles = StyleSheet.create({
  body: {
    paddingTop: spacing.sm,
  },
  imageWrap: {
    aspectRatio: 1,
    borderRadius: radii.xl,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.surface,
    overflow: 'hidden',
  },
  image: {
    width: '100%',
    height: '100%',
  },
  imageFallback: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.brightGreenSoft,
  },
  imageFallbackText: {
    fontFamily: fonts.display,
    fontSize: fontSizes.watermark,
    color: colors.deepGreen,
  },
  category: {
    marginTop: spacing.lg,
    marginBottom: spacing.sm,
  },
  description: {
    marginTop: spacing.md,
  },
  receiveCard: {
    marginTop: spacing.lg,
  },
  receiveText: {
    marginTop: spacing.xs,
  },
  noPost: {
    marginTop: spacing.base,
  },
  cta: {
    marginTop: spacing.lg,
  },
  backCta: {
    marginTop: spacing.md,
  },
});
